import { yupResolver } from '@hookform/resolvers/yup';
import ExpandIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, CircularProgress, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useTrackActionFormDirty } from 'components/ActionDialog/actionFormDirtyState';
import { indexOf, isBoolean } from 'lodash';
import { useSnackbar } from 'notistack';
import React, { ReactElement, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import * as yup from 'yup';

import { runSlideRegistrations } from 'api/platform';
import { getSlidesDataForArtifactsResults } from 'api/study';
import { ActionStepper } from 'components/StudyDashboard/ProceduresPage/Actions/ActionStepper';
import { createSlideManifestAndWarnings } from 'components/StudyDashboard/ProceduresPage/Actions/RunSlideRegistrations/helpers';
import {
  MetadataOptionKey,
  metadataOptions,
  SelectSlidesStep,
} from 'components/StudyDashboard/ProceduresPage/Actions/RunSlideRegistrations/SelectSlides';
import {
  RunSlideRegistrationsFormValues,
  runSlideRegistrationsOrder,
  RunSlideRegistrationsStep,
} from 'components/StudyDashboard/ProceduresPage/Actions/RunSlideRegistrations/types';
import { JobNameAndDescriptionStep } from 'components/StudyDashboard/ProceduresPage/Actions/utils';
import { Permission } from 'interfaces/permissionOption';
import { CasesParams } from 'utils/useCasesParams/types';
import { useMutationWithErrorSnackbar } from 'utils/useMutationWithErrorSnackbar';
import { usePermissions } from 'utils/usePermissions';

const SNACK_BAR_KEY_RUN_SLIDE_REGISTRATIONS = 'RUN_SLIDE_REGISTRATIONS';

const validationSchema = {
  [RunSlideRegistrationsStep.SelectSlides]: yup.object().shape({
    sourceStain: yup.string().required('Stain is required'),
    targetStain: yup.string().required('Stain is required'),
    slideManifest: yup
      .array()
      .of(
        yup.object().shape({
          slideId1: yup.string().required(),
          slideId2: yup.string().required(),
        })
      )
      .min(1, 'At least one pair of slides must be selected for registration'),
  }),
  [RunSlideRegistrationsStep.NameAndDescription]: yup.object({}),
};

export interface RunSlideRegistrationsProps {
  onClose: (event?: any, force?: boolean) => void;
  casesParams: CasesParams;
}

export const RunSlideRegistrations = (props: RunSlideRegistrationsProps): ReactElement => {
  const { onClose, casesParams } = props;
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
  const [stepWarnings, setStepWarnings] = useState<Record<number, string>>({});
  const [activeStep, setActiveStep] = useState(0);
  const currentValidationSchema = validationSchema[runSlideRegistrationsOrder[activeStep]];

  const methods = useForm<RunSlideRegistrationsFormValues>({
    mode: 'onChange',
    resolver: yupResolver(currentValidationSchema),
  });
  useTrackActionFormDirty(methods.formState.isDirty);

  const {
    register,
    handleSubmit,
    control,
    watch,
    trigger,
    formState: { errors },
  } = methods;

  const {
    data: slides,
    isFetching: isSlidesFetching,
    isLoading: isSlidesLoading,
  } = useQuery({
    queryKey: ['slidesDataForSlideRegistrations', casesParams],
    queryFn: ({ signal }) => getSlidesDataForArtifactsResults(casesParams, signal, ['negative_control']),
    enabled: Boolean(casesParams.filters?.studyId),
  });

  const [sourceSelectedMetadataOptions, setSourceSelectedMetadataOptions] = useState<MetadataOptionKey[]>(() => {
    const initialValue = watch(metadataOptions.negativeControl.sourceFieldName);
    return isBoolean(initialValue) ? (['negativeControl'] as MetadataOptionKey[]) : [];
  });

  const [targetSelectedMetadataOptions, setTargetSelectedMetadataOptions] = useState<MetadataOptionKey[]>(() => {
    const initialValue = watch(metadataOptions.negativeControl.targetFieldName);
    return isBoolean(initialValue) ? (['negativeControl'] as MetadataOptionKey[]) : [];
  });

  const createSlideManifest = () => {
    const { slideManifest, warningsString } = createSlideManifestAndWarnings({
      slides,
      sourceStain: watch('sourceStain'),
      targetStain: watch('targetStain'),
      sourceSelectedMetadataOptions,
      targetSelectedMetadataOptions,
      watch,
    });

    const stepIndex = indexOf(runSlideRegistrationsOrder, RunSlideRegistrationsStep.SelectSlides);
    setStepWarnings((prev) => ({
      ...prev,
      [stepIndex]: warningsString,
    }));

    methods.setValue('slideManifest', slideManifest, { shouldDirty: true, shouldValidate: true });
  };

  const [isStepFailed, setIsStepFailed] = useState<Record<number, boolean>>({});

  const checkValidationAndSetIsStepFailed = async (step: RunSlideRegistrationsStep) => {
    const stepIndex = indexOf(runSlideRegistrationsOrder, step);
    const isValid = await trigger();

    setIsStepFailed((prev) => ({
      ...prev,
      [stepIndex]: !isValid,
    }));
  };

  const runSlideRegistrationsMutation = useMutationWithErrorSnackbar({
    mutationFn: runSlideRegistrations,
    mutationDescription: 'run slide registrations',
    onSuccess: () => {
      enqueueSnackbar('Slide Registrations Started', { variant: 'success' });
      onClose(undefined, true);
    },
    onSettled() {
      closeSnackbar(SNACK_BAR_KEY_RUN_SLIDE_REGISTRATIONS);
    },
  });

  const onSubmit: SubmitHandler<RunSlideRegistrationsFormValues> = (data) => {
    runSlideRegistrationsMutation.mutate({
      studyId: casesParams.studyId ?? casesParams.filters?.studyId,
      jobName: data.jobName,
      jobDescription: data.jobDescription,
      slideManifest: data.slideManifest,
    });

    enqueueSnackbar({
      variant: 'success',
      message: (
        <Grid container>
          <Grid item>
            <Typography>Waiting for Slide Registrations to start</Typography>
          </Grid>
          <Grid item>
            <CircularProgress sx={{ marginLeft: 10 }} color="inherit" size={20} />
          </Grid>
        </Grid>
      ),
      key: SNACK_BAR_KEY_RUN_SLIDE_REGISTRATIONS,
      autoHideDuration: null,
    });
  };

  const steps = [
    {
      label: 'Select Slides',
      content: (
        <SelectSlidesStep
          methods={methods}
          checkValidationAndSetIsStepFailed={() =>
            checkValidationAndSetIsStepFailed(RunSlideRegistrationsStep.SelectSlides)
          }
          createSlideManifest={createSlideManifest}
          slides={slides}
          loading={isSlidesLoading || isSlidesFetching}
          sourceSelectedMetadataOptions={sourceSelectedMetadataOptions}
          setSourceSelectedMetadataOptions={setSourceSelectedMetadataOptions}
          targetSelectedMetadataOptions={targetSelectedMetadataOptions}
          setTargetSelectedMetadataOptions={setTargetSelectedMetadataOptions}
        />
      ),
      onNextOrBackClick: () => checkValidationAndSetIsStepFailed(RunSlideRegistrationsStep.SelectSlides),
    },
    {
      label: 'Job Name and Description',
      subLabel:
        activeStep !== indexOf(runSlideRegistrationsOrder, RunSlideRegistrationsStep.NameAndDescription) &&
        watch('jobName'),
      optional: true,
      content: <JobNameAndDescriptionStep control={control} errors={errors} register={register} />,
    },
  ];

  const { hasPermission } = usePermissions();
  const canViewJsonJobParams = hasPermission(Permission.ViewJsonJobParams);

  return (
    <>
      <ActionStepper
        handleSubmit={handleSubmit(onSubmit)}
        steps={steps}
        setActiveStepForValidation={setActiveStep}
        isStepFailed={isStepFailed}
        stepWarnings={stepWarnings}
      />
      {canViewJsonJobParams && (
        <Accordion>
          <AccordionSummary expandIcon={<ExpandIcon />}>Slide Registrations Params Summary (JSON)</AccordionSummary>
          <AccordionDetails>
            <Typography component="pre">
              {JSON.stringify(
                {
                  jobName: watch('jobName'),
                  jobDescription: watch('jobDescription'),
                  slideManifest: watch('slideManifest'),
                },
                null,
                2
              )}
            </Typography>
          </AccordionDetails>
        </Accordion>
      )}
    </>
  );
};
